import { useState, useEffect } from 'react';
import { CirclePlus } from 'lucide-react';
import { useParams } from 'react-router-dom';

import Certification from '@/app/certification/Certification';

import Error from '@/components/common/Error';
import { Button } from '@/components/ui/button';
import Spinner from '@/components/common/Spinner';

import {
  usePostCertification,
  useListCertifications
} from '@/data/certification';
import { useCoachActivity } from '@/data/coachActivity';

const ActivityCertificationsForm = ({ onDirtyChange }) => {
  const params = useParams();
  const coachActivity = useCoachActivity(params.coachActivityId);
  const postCertification = usePostCertification();
  const certifications = useListCertifications({
    coachActivityId: params.coachActivityId
  });
  const [apiError, setApiError] = useState('');
  const [isCreatingCertification, setIsCreatingCertification] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleAddCertification = async () => {
    setSubmitting(true);
    try {
      const response = await postCertification({
        coachActivityId: params.coachActivityId
      });
      // Update the certifications list
      certifications.mutate(
        (currentCertifications) => [...currentCertifications, response],
        false
      );
    } catch (err) {
      setApiError(err.message);
    }
    setSubmitting(false);
  };

  useEffect(() => {
    const createInitialCertification = async () => {
      if (
        !isCreatingCertification &&
        coachActivity.data?.id &&
        certifications.data &&
        certifications.data.length === 0
      ) {
        setIsCreatingCertification(true);
        try {
          const response = await postCertification({
            coachActivityId: params.coachActivityId
          });
          // Update the certifications list
          certifications.mutate(
            (currentCertifications) => [...currentCertifications, response],
            false
          );
        } catch (err) {
          setApiError(err.message);
        } finally {
          setIsCreatingCertification(false);
        }
      }
    };

    createInitialCertification();
  }, [
    certifications,
    coachActivity.data,
    certifications.data,
    postCertification,
    isCreatingCertification,
    params.coachActivityId
  ]);

  if (certifications.isLoading && !certifications.data) {
    return null;
  }

  return (
    <div>
      <div className="mb-8">
        <h3>Certifications</h3>
        <span className="text-muted-foreground">
          Please upload your {coachActivity.data.activity.title} specific
          coaching certifications and supporting documents.
        </span>
        <br />
        <br />
        <span className="text-muted-foreground">
          Minimum required certifications for{' '}
          {coachActivity.data.activity.title}:
        </span>
        <ul className="list-disc list-inside mt-2 ml-4">
          {coachActivity.data.activity.activityQualifications.map(
            (qualification) => (
              <li
                key={qualification.id}
                className="text-sm text-muted-foreground"
              >
                {qualification.name}
              </li>
            )
          )}
        </ul>
      </div>
      <div className="flex flex-col gap-4">
        {certifications.data.map((certification) => (
          <Certification
            key={certification.id}
            certification={certification}
            listObj={{ coachActivityId: params.coachActivityId }}
            placeholder="Certification Name..."
            onDirtyChange={onDirtyChange}
          />
        ))}
      </div>
      {apiError && <Error error={apiError} />}
      <div className="flex justify-center py-8">
        <Button
          variant="ghost"
          onClick={handleAddCertification}
          disabled={submitting}
        >
          {submitting && <Spinner />}
          {!submitting && <CirclePlus />}
          &nbsp; Add Another Certification
        </Button>
      </div>
    </div>
  );
};

export default ActivityCertificationsForm;
